import React from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, ArrowRight } from "lucide-react";
import { useStore } from "../context/StoreContext";

export default function CartDrawer() {
    const { cartOpen, setCartOpen, items, itemKey, updateQty, removeItem, subtotal, products } = useStore();
    const suggestion = products.find((p) => !items.some((i) => i.product_id === p.id));

    return (
        <AnimatePresence>
            {cartOpen && (
                <>
                    <motion.div
                        data-testid="cart-backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                        onClick={() => setCartOpen(false)}
                        className="fixed inset-0 z-[160] bg-[rgba(10,10,10,0.75)]"
                    />
                    <motion.aside
                        data-testid="cart-drawer"
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                        className="fixed right-0 top-0 z-[170] flex h-full w-full max-w-md flex-col border-l border-[rgba(10,10,10,0.12)] bg-[#F2F0EA]"
                    >
                        <div className="flex items-center justify-between border-b border-[rgba(10,10,10,0.12)] px-6 py-5">
                            <h2 className="font-display text-xs font-bold uppercase tracking-[0.35em]">Your Bag</h2>
                            <button data-testid="cart-close-button" onClick={() => setCartOpen(false)} aria-label="Close bag">
                                <X size={18} strokeWidth={1.5} />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto px-6 py-6">
                            {items.length === 0 ? (
                                <p data-testid="cart-empty-message" className="mt-10 text-center text-xs uppercase tracking-[0.25em] text-[#6B6A66]">
                                    Your bag is empty
                                </p>
                            ) : (
                                <ul className="space-y-6">
                                    <AnimatePresence>
                                        {items.map((i) => {
                                            const k = itemKey(i);
                                            return (
                                                <motion.li
                                                    key={k}
                                                    layout
                                                    initial={{ opacity: 0, y: 16 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, x: 40 }}
                                                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                                                    className="flex gap-4"
                                                    data-testid={`cart-item-${k}`}
                                                >
                                                    <img src={i.image} alt={i.name} className="h-28 w-22 w-[88px] object-cover" />
                                                    <div className="flex flex-1 flex-col">
                                                        <p className="font-display text-[11px] font-bold uppercase tracking-[0.15em]">{i.name}</p>
                                                        <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-[#8E8D8A]">
                                                            {i.color} / {i.size}
                                                            {i.print_type === "RAISED" && " / Raised Reality"}
                                                        </p>
                                                        <div className="mt-auto flex items-center justify-between">
                                                            <div className="flex items-center border border-[rgba(10,10,10,0.12)]">
                                                                <button
                                                                    data-testid={`cart-qty-minus-${k}`}
                                                                    onClick={() => updateQty(k, i.qty - 1)}
                                                                    className="px-2 py-1"
                                                                    aria-label="Decrease quantity"
                                                                >
                                                                    <Minus size={12} />
                                                                </button>
                                                                <span className="w-6 text-center text-xs">{i.qty}</span>
                                                                <button
                                                                    data-testid={`cart-qty-plus-${k}`}
                                                                    onClick={() => updateQty(k, i.qty + 1)}
                                                                    className="px-2 py-1"
                                                                    aria-label="Increase quantity"
                                                                >
                                                                    <Plus size={12} />
                                                                </button>
                                                            </div>
                                                            <span className="text-xs tracking-wider">${i.price * i.qty} CAD</span>
                                                        </div>
                                                    </div>
                                                    <button
                                                        data-testid={`cart-remove-${k}`}
                                                        onClick={() => removeItem(k)}
                                                        className="self-start text-[#6B6A66] transition-colors hover:text-[#0A0A0A]"
                                                        aria-label="Remove item"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </motion.li>
                                            );
                                        })}
                                    </AnimatePresence>
                                </ul>
                            )}
                            {suggestion && items.length > 0 && (
                                <div className="mt-10 border-t border-[rgba(10,10,10,0.12)] pt-6">
                                    <p className="mb-4 text-[9px] uppercase tracking-[0.35em] text-[#6B6A66]">Complete the look</p>
                                    <Link
                                        data-testid="cart-suggestion-link"
                                        to={`/product/${suggestion.id}`}
                                        onClick={() => setCartOpen(false)}
                                        className="group flex items-center gap-4"
                                    >
                                        <img src={suggestion.front} alt={suggestion.name} className="h-16 w-[52px] object-cover" />
                                        <div>
                                            <p className="link-underline font-display text-[10px] font-bold uppercase tracking-[0.15em]">
                                                {suggestion.name}
                                            </p>
                                            <p className="mt-1 text-[10px] text-[#8E8D8A]">${suggestion.price} CAD</p>
                                        </div>
                                    </Link>
                                </div>
                            )}
                        </div>
                        {items.length > 0 && (
                            <div className="border-t border-[rgba(10,10,10,0.12)] px-6 py-6">
                                <div className="mb-5 flex items-center justify-between">
                                    <span className="text-[10px] uppercase tracking-[0.3em] text-[#8E8D8A]">Subtotal</span>
                                    <span data-testid="cart-subtotal" className="font-display text-sm font-bold tracking-wider">
                                        ${subtotal} CAD
                                    </span>
                                </div>
                                <Link
                                    data-testid="checkout-button"
                                    to="/checkout"
                                    onClick={() => setCartOpen(false)}
                                    className="group flex w-full items-center justify-center gap-3 bg-[#0A0A0A] py-4 font-display text-[11px] font-bold uppercase tracking-[0.3em] text-[#F2F0EA] transition-colors hover:bg-white"
                                >
                                    Checkout
                                    <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1" />
                                </Link>
                                <p className="mt-3 text-center text-[9px] uppercase tracking-[0.25em] text-[#6B6A66]">
                                    Complimentary shipping over $300
                                </p>
                            </div>
                        )}
                    </motion.aside>
                </>
            )}
        </AnimatePresence>
    );
}
